import { Phone, MapPin, MessageCircle, ExternalLink } from "lucide-react";

const QR = (data, dark = false) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=160x160&bgcolor=${dark ? "111111" : "F5F1E8"}&color=${dark ? "F5F1E8" : "111111"}&data=${encodeURIComponent(data)}`;

export default function PropertyContact({ contact, variant = "light" }) {
  const isDark = variant === "dark";
  const textPrimary = isDark ? "text-ivory" : "text-charcoal";
  const textMuted = isDark ? "text-ivory/55" : "text-charcoal/60";
  const iconColor = isDark ? "text-gold/70" : "text-gold";
  const borderColor = isDark ? "border-ivory/10" : "border-charcoal/15";
  const qrBg = isDark ? "bg-ivory/[0.04]" : "bg-charcoal/[0.04]";

  const whatsappUrl = `https://wa.me/${contact.whatsappRaw}?text=${encodeURIComponent(`Hello ${contact.name}`)}`;
  const mapQr = QR(contact.mapUrl, isDark);
  const waQr = QR(whatsappUrl, isDark);

  return (
    <div data-testid={`contact-block-${contact.phoneRaw}`} className={`border-t ${borderColor} pt-8 md:pt-10`}>
      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-16 items-start">
        {/* Address + Phone + Actions */}
        <div className="space-y-6">
          <p className="overline-text text-gold mb-2">Visit & Reach Us</p>
          <div className="space-y-5">
            <div className="flex items-start gap-3">
              <MapPin size={16} strokeWidth={1.2} className={`mt-1 ${iconColor} shrink-0`} />
              <a
                href={contact.mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`contact-address-${contact.phoneRaw}`}
                className={`text-sm ${textMuted} leading-relaxed hover:underline underline-offset-4`}
              >
                {contact.address}
              </a>
            </div>
            <div className="flex items-center gap-3">
              <Phone size={16} strokeWidth={1.2} className={`${iconColor} shrink-0`} />
              <a
                href={`tel:${contact.phone.replace(/\s/g, "")}`}
                data-testid={`contact-phone-${contact.phoneRaw}`}
                className={`text-sm ${textMuted} hover:${textPrimary} transition-colors`}
              >
                {contact.phone}
              </a>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 pt-2">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`contact-whatsapp-${contact.phoneRaw}`}
              className={`inline-flex items-center gap-2 px-5 py-3 ${
                isDark
                  ? "border border-ivory/25 text-ivory hover:bg-ivory/10"
                  : "bg-charcoal text-ivory hover:bg-charcoal/85"
              } text-[11px] tracking-[0.18em] uppercase font-medium transition-all duration-300`}
            >
              <MessageCircle size={13} strokeWidth={1.5} />
              WhatsApp
            </a>
            <a
              href={contact.mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`contact-directions-${contact.phoneRaw}`}
              className={`inline-flex items-center gap-2 px-5 py-3 border ${
                isDark
                  ? "border-ivory/15 text-ivory/85 hover:bg-ivory/5"
                  : "border-charcoal/20 text-charcoal/80 hover:bg-charcoal/5"
              } text-[11px] tracking-[0.18em] uppercase font-medium transition-all duration-300`}
            >
              <ExternalLink size={13} strokeWidth={1.5} />
              Directions
            </a>
          </div>
        </div>

        {/* QR codes */}
        <div className="grid grid-cols-2 gap-4 sm:gap-6">
          <div className={`${qrBg} p-4 sm:p-5 flex flex-col items-center gap-3`}>
            <img
              src={mapQr}
              alt={`Map QR code for ${contact.name}`}
              data-testid={`qr-map-${contact.phoneRaw}`}
              className="w-24 h-24 sm:w-28 sm:h-28"
              loading="lazy"
            />
            <p className={`text-[10px] ${textMuted} tracking-[0.15em] uppercase text-center`}>
              Scan for<br />Directions
            </p>
          </div>
          <div className={`${qrBg} p-4 sm:p-5 flex flex-col items-center gap-3`}>
            <img
              src={waQr}
              alt={`WhatsApp QR code for ${contact.name}`}
              data-testid={`qr-whatsapp-${contact.phoneRaw}`}
              className="w-24 h-24 sm:w-28 sm:h-28"
              loading="lazy"
            />
            <p className={`text-[10px] ${textMuted} tracking-[0.15em] uppercase text-center`}>
              Scan to<br />WhatsApp
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
